import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import ms from 'milsymbol';
import parse from 'html-react-parser';
import { Faction, FactionCreate, FactionUpdate, AffiliationType, getAffiliationSidcDigit } from '../../types/orbat';
import { buildSidc } from '../unit/SidcBuilder/sidcOptions';

interface AddFactionModalProps {
    show: boolean;
    onHide: () => void;
    onSubmit: (data: FactionCreate | FactionUpdate) => Promise<void>;
    faction?: Faction | null; // If provided, modal is in edit mode
    affiliations: AffiliationType[]; // Available affiliation types from API
    isLoading?: boolean;
    error?: string | null;
}

/**
 * Modal for creating or editing a faction.
 */
const AddFactionModal: React.FC<AddFactionModalProps> = ({
    show,
    onHide,
    onSubmit,
    faction,
    affiliations,
    isLoading = false,
    error = null,
}) => {
    const [name, setName] = useState('');
    const [affiliationId, setAffiliationId] = useState('');
    const [validated, setValidated] = useState(false);

    const isEditMode = !!faction;

    // Reset form when modal opens/closes or faction changes
    useEffect(() => {
        if (show) {
            if (faction) {
                setName(faction.name);
                setAffiliationId(faction.affiliation_id);
            } else {
                setName('');
                // Default to first affiliation if available
                setAffiliationId(affiliations.length > 0 ? affiliations[0].id : '');
            }
            setValidated(false);
        }
    }, [show, faction, affiliations]);

    // Get selected affiliation for symbol rendering
    const selectedAffiliation = useMemo(() => {
        return affiliations.find((a) => a.id === affiliationId);
    }, [affiliations, affiliationId]);

    // Generate preview symbol using APP-6D standard
    // Shows a generic HQ unit symbol with the faction's affiliation color
    const previewSymbol = useMemo(() => {
        const identityDigit = selectedAffiliation
            ? getAffiliationSidcDigit(selectedAffiliation.name)
            : '3'; // Default to Friend
        // Build APP-6D compliant SIDC:
        // - Identity: from affiliation
        // - Symbol Set: 10 (Land Unit)
        // - Status: 0 (Present)
        // - HQTFD: 2 (Headquarters)
        // - Echelon: 00 (Unspecified)
        // - Unit Type: 1100000000 (Command & Control)
        const sidc = buildSidc(identityDigit, '10', '0', '2', '00', '0000000000');
        console.log(identityDigit)
        console.log(affiliations)
        const symbol = new ms.Symbol(sidc, { size: 60 });
        return symbol.asSVG();
    }, [selectedAffiliation]);

    const handleClose = () => {
        setName('');
        setAffiliationId('friend');
        setValidated(false);
        onHide();
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const form = event.currentTarget;

        if (form.checkValidity() === false) {
            event.stopPropagation();
            setValidated(true);
            return;
        }

        setValidated(true);

        try {
            if (isEditMode) {
                await onSubmit({
                    name: name.trim(),
                    affiliation_id: affiliationId,
                } as FactionUpdate);
            } else {
                await onSubmit({
                    name: name.trim(),
                    affiliation_id: affiliationId,
                } as FactionCreate);
            }
            handleClose();
        } catch (err) {
            // Error is handled by parent component
            console.error('Faction submission failed:', err);
        }
    };

    return (
        <Modal show={show} onHide={handleClose} centered>
            <Modal.Header closeButton>
                <Modal.Title>
                    {isEditMode ? 'Edit Faction' : 'Create New Faction'}
                </Modal.Title>
            </Modal.Header>
            <Form noValidate validated={validated} onSubmit={handleSubmit}>
                <Modal.Body>
                    {error && (
                        <Alert variant="danger" className="d-flex align-items-center">
                            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                            {error}
                        </Alert>
                    )}

                    {/* Symbol Preview */}
                    <div className="faction-modal__preview">
                        {parse(previewSymbol)}
                    </div>

                    <Form.Group className="mb-3" controlId="factionName">
                        <Form.Label>Faction Name</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Enter faction name (e.g., BLUFOR, OPFOR)"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            required
                            minLength={1}
                            maxLength={127}
                            disabled={isLoading}
                            autoFocus
                        />
                        <Form.Control.Feedback type="invalid">
                            Please enter a faction name (1-127 characters).
                        </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="factionAffiliation">
                        <Form.Label>Affiliation</Form.Label>
                        <Form.Select
                            value={affiliationId}
                            onChange={(e) => setAffiliationId(e.target.value)}
                            disabled={isLoading || affiliations.length === 0}
                            required
                        >
                            {affiliations.length === 0 ? (
                                <option value="">Loading affiliations...</option>
                            ) : (
                                affiliations.map((affiliation) => (
                                    <option key={affiliation.id} value={affiliation.id}>
                                        {affiliation.name}
                                    </option>
                                ))
                            )}
                        </Form.Select>
                        <Form.Text className="text-muted">
                            Determines the color and shape of military symbols.
                        </Form.Text>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose} disabled={isLoading}>
                        Cancel
                    </Button>
                    <Button variant="primary" type="submit" disabled={isLoading}>
                        {isLoading ? (
                            <>
                                <Spinner
                                    as="span"
                                    animation="border"
                                    size="sm"
                                    role="status"
                                    aria-hidden="true"
                                    className="me-2"
                                />
                                {isEditMode ? 'Saving...' : 'Creating...'}
                            </>
                        ) : (
                            isEditMode ? 'Save Changes' : 'Create Faction'
                        )}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

export default AddFactionModal;
